import React, { useState } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, X, Globe, RotateCcw } from "lucide-react";
import formatApiError from "../../lib/apiError";
import useActiveInactive from "../../lib/useActiveInactive";
import ActiveInactiveTabs from "../../components/ActiveInactiveTabs";

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => ({ withCredentials: true });
const BASE = `${API}/api/countries`;

const EMPTY_FORM = { name: "", country_code: "", country_phone_code: "", description: "" };

export default function CountriesPage() {
  const { view, setView, list: countries, inactiveCount, loading, error, reload } = useActiveInactive(BASE);

  const [modal, setModal]         = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [formErr, setFormErr]     = useState("");
  const [saving, setSaving]       = useState(false);

  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting]   = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const onChange = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const openAdd  = () => { setForm(EMPTY_FORM); setFormErr(""); setModal({ mode: "add", country: null }); };
  const openEdit = (c) => {
    setForm({ name: c.name, country_code: c.country_code, country_phone_code: c.country_phone_code, description: c.description || "" });
    setFormErr(""); setModal({ mode: "edit", country: c });
  };
  const closeModal = () => { setModal(null); setFormErr(""); };

  const handleSave = async () => {
    if (!form.name.trim())               { setFormErr("Name is required."); return; }
    if (!form.country_code.trim())       { setFormErr("Country code is required."); return; }
    if (!form.country_phone_code.trim()) { setFormErr("Phone code is required."); return; }
    setSaving(true); setFormErr("");
    try {
      if (modal.mode === "add") await axios.post(BASE, form, ax());
      else                       await axios.put(`${BASE}/${modal.country.id}`, form, ax());
      closeModal(); reload();
    } catch (e) { setFormErr(formatApiError(e, "Something went wrong.")); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try { await axios.delete(`${BASE}/${confirmId}`, ax()); setConfirmId(null); reload(); }
    catch (e) { alert(formatApiError(e, "Could not delete.")); setConfirmId(null); }
    finally { setDeleting(false); }
  };

  const handleRestore = async (id) => {
    setRestoringId(id);
    try { await axios.post(`${BASE}/${id}/restore`, {}, ax()); reload(); }
    catch (e) { alert(formatApiError(e, "Could not restore.")); }
    finally { setRestoringId(null); }
  };

  return (
    <div className="page-wrap">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">Countries</h1>
          <p className="page-sub">Manage the country reference list used across the system.</p>
        </div>
        {view === "active" && (
          <button className="btn-primary" onClick={openAdd} data-testid="add-country-btn">
            <Plus size={16} /> Add Country
          </button>
        )}
      </div>

      <ActiveInactiveTabs view={view} onChange={setView} inactiveCount={inactiveCount} testIdPrefix="countries-view" />

      {loading ? <div className="table-loading">Loading…</div>
       : error  ? <div className="table-error">{error}</div>
       : (
        <div className="data-card">
          {countries.length === 0 ? (
            <div className="empty-state">
              <Globe size={32} color="#43E0FF" />
              {view === "active"
                ? <p>No countries yet. Click <strong>Add Country</strong> to create one.</p>
                : <p>No inactive countries.</p>}
            </div>
          ) : (
            <table className="data-table" data-testid="countries-table">
              <thead>
                <tr>
                  <th style={{ width: 48 }}>#</th>
                  <th>Name</th>
                  <th style={{ width: 130 }}>Country Code</th>
                  <th style={{ width: 130 }}>Phone Code</th>
                  <th>Description</th>
                  <th style={{ width: 110 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {countries.map((c, i) => (
                  <tr key={c.id} data-testid={`country-row-${c.id}`}>
                    <td className="td-num">{i + 1}</td>
                    <td className="td-name">{c.name}</td>
                    <td><span className="subject-pill-code">{c.country_code}</span></td>
                    <td className="td-desc">{c.country_phone_code}</td>
                    <td className="td-desc">{c.description || <span className="td-empty">—</span>}</td>
                    <td>
                      <div className="action-btns">
                        {view === "active" ? (
                          <>
                            <button className="action-edit"   onClick={() => openEdit(c)}        data-testid={`edit-country-${c.id}`}   title="Edit"><Pencil size={15} /></button>
                            <button className="action-delete" onClick={() => setConfirmId(c.id)} data-testid={`delete-country-${c.id}`} title="Delete"><Trash2 size={15} /></button>
                          </>
                        ) : (
                          <button className="action-restore" onClick={() => handleRestore(c.id)} disabled={restoringId === c.id} data-testid={`restore-country-${c.id}`} title="Restore"><RotateCcw size={15} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box modal-box-md" onClick={(e) => e.stopPropagation()} data-testid="country-modal">
            <div className="modal-head">
              <h2 className="modal-title">{modal.mode === "add" ? "Add Country" : "Edit Country"}</h2>
              <button className="modal-close" onClick={closeModal}><X size={18} /></button>
            </div>
            {formErr && <div className="form-error" data-testid="form-error">{formErr}</div>}
            <div className="modal-body">
              <div className="field">
                <label className="field-label">Name <span className="required">*</span></label>
                <input className="field-input" placeholder="India" value={form.name}
                  onChange={onChange("name")} maxLength={120} autoFocus data-testid="country-name-input" />
              </div>
              <div className="field-row">
                <div className="field">
                  <label className="field-label">Country Code <span className="required">*</span></label>
                  <input className="field-input" placeholder="IN" value={form.country_code}
                    onChange={onChange("country_code")} maxLength={5}
                    style={{ textTransform: "uppercase" }} data-testid="country-code-input" />
                </div>
                <div className="field">
                  <label className="field-label">Phone Code <span className="required">*</span></label>
                  <input className="field-input" placeholder="+91" value={form.country_phone_code}
                    onChange={onChange("country_phone_code")} maxLength={10} data-testid="country-phone-code-input" />
                </div>
              </div>
              <div className="field">
                <label className="field-label">Description</label>
                <textarea className="field-input field-textarea" placeholder="Optional notes"
                  value={form.description} onChange={onChange("description")} rows={3} maxLength={600}
                  data-testid="country-description-input" />
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving} data-testid="save-country-btn">
                {saving ? <span className="btn-spinner" /> : modal.mode === "add" ? "Add Country" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmId && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="modal-box modal-box-sm" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm-modal">
            <div className="modal-head">
              <h2 className="modal-title">Delete Country</h2>
              <button className="modal-close" onClick={() => setConfirmId(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <p className="confirm-text">Move this country to <strong>Inactive</strong>? You cannot deactivate a country that still has active states.</p>
            </div>
            <div className="modal-foot">
              <button className="btn-ghost" onClick={() => setConfirmId(null)}>Cancel</button>
              <button className="btn-danger" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-btn">
                {deleting ? <span className="btn-spinner" /> : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
